import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { listAgents, listRuns, type AgentRun } from './api';
import { agentNameMap, TriggerBadge } from './triggerLabel';

type StatusFilter = 'all' | 'pending' | 'running' | 'done' | 'failed';

const STATUSES: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'running', label: 'Running' },
  { value: 'done', label: 'Done' },
  { value: 'failed', label: 'Failed' },
];

export function AgentRunsTab({ orgId, agentId }: { orgId: string; agentId: string }) {
  const [filter, setFilter] = useState<StatusFilter>('all');
  const runs = useQuery({
    queryKey: ['runs', orgId, agentId],
    queryFn: () => listRuns(orgId, agentId),
    enabled: !!orgId,
    refetchInterval: 5_000,
  });
  // Pull every agent in the org so the trigger badge can render
  // 'Agent · Trading Bot' (the display name) instead of the bare UUID
  // when another agent kicked this run via A2A.
  const orgAgents = useQuery({
    queryKey: ['agents', orgId],
    queryFn: () => listAgents(orgId),
    enabled: !!orgId,
  });
  const names = useMemo(() => agentNameMap(orgAgents.data), [orgAgents.data]);

  const filtered = useMemo(() => {
    const all = runs.data ?? [];
    if (filter === 'all') return all;
    return all.filter((r) => r.status === filter);
  }, [runs.data, filter]);

  return (
    <Card>
      <CardHeader
        title="Runs"
        description="Click any row to inspect the trace. Polled every 5 seconds."
      />
      <CardBody className="space-y-3 p-0">
        <div className="flex items-center gap-2 border-b border-line-soft px-4 py-3">
          {STATUSES.map((s) => (
            <FilterPill
              key={s.value}
              active={filter === s.value}
              onClick={() => setFilter(s.value)}
            >
              {s.label}
            </FilterPill>
          ))}
          <div className="ml-auto font-mono text-[11px] text-ink-tertiary">
            {filtered.length} of {runs.data?.length ?? 0}
          </div>
        </div>

        {runs.isLoading && <p className="px-4 py-6 text-sm text-ink-tertiary">Loading…</p>}
        {!runs.isLoading && filtered.length === 0 && (
          <p className="px-4 py-6 text-sm text-ink-tertiary">No runs matching this filter.</p>
        )}
        {filtered.length > 0 && (
          <table className="w-full text-left text-sm">
            <thead className="border-b border-line-soft text-xs uppercase tracking-eyebrow text-ink-tertiary">
              <tr>
                <th className="px-4 py-2 font-medium">When</th>
                <th className="px-4 py-2 font-medium">Trigger</th>
                <th className="px-4 py-2 font-medium">Objective</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Outcome</th>
                <th className="px-4 py-2 font-medium">Tokens</th>
                <th className="px-4 py-2 font-medium" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <RunRow key={r.id} agentId={agentId} r={r} names={names} />
              ))}
            </tbody>
          </table>
        )}
      </CardBody>
    </Card>
  );
}

function RunRow({
  agentId,
  r,
  names,
}: {
  agentId: string;
  r: AgentRun;
  names: Record<string, string>;
}) {
  return (
    <tr className="border-t border-line-soft hover:bg-surface-soft/40">
      <td className="px-4 py-2 text-xs text-ink-secondary">
        {new Date(r.created_at).toLocaleString()}
      </td>
      <td className="px-4 py-2">
        <TriggerBadge raw={r.triggered_by} agentNames={names} />
      </td>
      <td className="px-4 py-2 text-sm text-ink-primary">{truncate(r.objective, 80)}</td>
      <td className="px-4 py-2">
        <StatusBadge status={r.status} />
      </td>
      <td className="px-4 py-2 text-xs text-ink-secondary">{r.outcome ?? '—'}</td>
      <td className="px-4 py-2 font-mono text-xs text-ink-secondary">
        {r.tokens_input}/{r.tokens_output}
      </td>
      <td className="px-4 py-2 text-right">
        <Link
          to={`/agents/${agentId}/runs/${r.id}`}
          className="text-sm font-medium text-bit2me hover:underline"
        >
          View trace →
        </Link>
      </td>
    </tr>
  );
}

function FilterPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-eyebrow transition-colors ${
        active
          ? 'border-bit2me bg-bit2me/10 text-bit2me'
          : 'border-line bg-surface-white text-ink-secondary hover:border-ink-secondary'
      }`}
    >
      {children}
    </button>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    pending: 'bg-surface-soft text-ink-tertiary',
    running: 'bg-amber-100 text-amber-700',
    done: 'bg-ok/10 text-ok',
    failed: 'bg-danger/10 text-danger',
  };
  return (
    <span className={`rounded px-1.5 py-0.5 font-mono text-[10px] uppercase ${map[status] ?? ''}`}>
      {status}
    </span>
  );
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n)}…` : s;
}
